import { FetchResult, MutationResult, useMutation } from '@apollo/client';

import {
  GET_ASC_PROJECTS,
  UPDATE_CONTRIBUTOR_ALLOCATIONS_FOR_ASC,
  UpdateContributorAllocationsForAscMutation,
  UpdateContributorAllocationsForAscMutationVariables,
  useCurrentCompanyId,
  GetAscProjectsQueryVariables,
  evictCache,
} from '@neo.tax/shared';

import { useAscContext } from '../context';
import { ContributorAllocationsInPeriod } from '../types';
import { buildContributorAllocationPayload } from '../utils/contributor-allocation-payload.utils';

type UseUpdateContributorAllocationsMutation = {
  updateContributorAllocations: (
    allocationsInPeriod: ContributorAllocationsInPeriod,
  ) => Promise<FetchResult<UpdateContributorAllocationsForAscMutation>>;
} & MutationResult<UpdateContributorAllocationsForAscMutation>;

const UPDATE_CONTRIBUTOR_ALLOCATION_REQUEST_KEY = 'UPDATE_CONTRIBUTOR_ALLOCATION_REQUEST_KEY';

/**
 * A custom hook to use for updating contributor allocations.
 *
 * @param {string} params.contributorId - The contributor's id to update.
 * @param {MonthYearPeriod} params.period - The MonthYearPeriod for the update.
 * @param {() => void} params.onSuccess - Function to execute on successful mutation.
 */
export const useUpdateContributorAllocationsMutation = ({
  contributorId,
  onSuccess,
}: {
  contributorId: string;
  onSuccess: () => void;
}): UseUpdateContributorAllocationsMutation => {
  const companyId = useCurrentCompanyId();
  const { fiscalYearId, periodName } = useAscContext();

  const refetchQueries = [
    {
      query: GET_ASC_PROJECTS,
      variables: { companyId, fiscalYearId, periodName } satisfies GetAscProjectsQueryVariables,
    },
  ];

  const [mutate, mutationResult] = useMutation<
    UpdateContributorAllocationsForAscMutation,
    UpdateContributorAllocationsForAscMutationVariables
  >(UPDATE_CONTRIBUTOR_ALLOCATIONS_FOR_ASC, {
    awaitRefetchQueries: false,
    refetchQueries,
    onCompleted: () => {
      onSuccess();
    },
    update: (cache) => {
      evictCache(cache, [
        'getAscProjectTeamMembers',
        'getAscWages',
        'getAscWageSummary',
        'getContributor',
        'getAscContributorProjectAllocations',
      ]);
    },
    context: {
      serializationKey: UPDATE_CONTRIBUTOR_ALLOCATION_REQUEST_KEY,
      debounceKey: UPDATE_CONTRIBUTOR_ALLOCATION_REQUEST_KEY,
      errorToast: {
        title: 'Failed to update percentages',
        description: 'An unexpected error happened while updating. Please try again.',
      },
      successToast: {
        title: 'Successfully updated percentages',
      },
    },
  });

  const updateContributorAllocations = (
    allocationsInPeriod: ContributorAllocationsInPeriod,
  ): Promise<FetchResult<UpdateContributorAllocationsForAscMutation>> => {
    const { allocationPercents, nonProjectPercent } = buildContributorAllocationPayload(allocationsInPeriod);

    return mutate({
      variables: {
        fiscalYearId,
        periodName,
        input: {
          contributorId,
          nonProjectPercent,
          allocationPercents,
        },
      },
    });
  };

  return { updateContributorAllocations, ...mutationResult };
};
